import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import useChartById from '@hooks/api/useChartById';
import { setDashboardFilter } from '@stores/DashboardFilters/slice';
import {
  useEditProjectionsMutation,
  useGetFundingPercentageQuery,
  useLazyGetPredictionsQuery,
  useUpdateFundingPercentageMutation,
} from '@api/projections';
import FinancialProjectionsTable from './FinancialProjectionsTable/FinancialProjectionsTable';
import { processCombinedCohortData } from './FinancialProjectionsTable/tableDataUtil';
import {
  FinancialProjectionChartTypes,
  PredictionDetails,
  ProjectionValueUpdateType,
} from './types';
import DownloadChart from '../DownloadChart';
import NoData from './NoData';
import AddSharpIcon from '@mui/icons-material/AddSharp';
import RemoveSharpIcon from '@mui/icons-material/RemoveSharp';
import CombinedProjectionsChart from './CombinedProjectionsChart';
import { Circle, TrendingUp } from '@mui/icons-material';
interface CombinedCohortProjectionsProps {
  submoduleId: string;
  selectedGame: { id: string; label: string };
  setDashboardLoader: React.Dispatch<React.SetStateAction<boolean>>;
  dashboardCharts: {
    id: string;
    name: string;
    chartType: string;
    metadata: string;
  }[];
}

const CombinedCohortsProjections: React.FC<CombinedCohortProjectionsProps> = ({
  submoduleId,
  selectedGame,
  setDashboardLoader,
  dashboardCharts,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [chartData, setChartData] = useState<
    {
      id: string;
      name: string;
      columns: any[];
      rows: any[];
      meta: any;
      pricingDetails: string[];
    }[]
  >([]);
  const [componentLoader, setComponentLoader] = useState<boolean>(true);
  const [predictionDetails, setPredictionDetails] = useState<PredictionDetails | undefined>(
    undefined,
  );
  const [refetchLoader, setRefetchLoader] = useState<boolean>(false);

  const { getChartDataById } = useChartById();
  const [getPredictions, { isError: isPredictionsFetchError }] = useLazyGetPredictionsQuery();
  const [editProjections, { isSuccess: isEditProjectionsSuccess }] = useEditProjectionsMutation();

  const { data: fundingScenarioData } = useGetFundingPercentageQuery(
    {
      submoduleId,
      predictionId: predictionDetails?.requestId || '',
    },
    {
      skip: !predictionDetails?.requestId,
    },
  );

  const [pvxFundingPercentage, setPvxFundingPercentage] = useState<number>(
    (fundingScenarioData?.data?.value || 0.8) * 100,
  );

  useEffect(() => {
    if (fundingScenarioData?.data?.value != null) {
      setPvxFundingPercentage(fundingScenarioData.data.value * 100);
    }
  }, [fundingScenarioData?.data?.value]);

  const [updateFundingPercentage, { isSuccess: isUpdateFundingPercentageSuccess }] =
    useUpdateFundingPercentageMutation();

  const chartsList = [
    FinancialProjectionChartTypes.FINANCIAL_PROJECTIONS_COMBINED_VIEW,
    FinancialProjectionChartTypes.FINANCIAL_PROJECTIONS_FUNDING_SCENARIO,
    FinancialProjectionChartTypes.FINANCIAL_PROJECTIONS_EBITDA,
    FinancialProjectionChartTypes.FINANCIAL_PROJECTIONS_END_CASH_BALANCE,
  ];
  const tabsChartsList = dashboardCharts.filter((chart: any) =>
    chartsList.includes(chart.chartType),
  );

  const fetchDashboardData = async (filters?: Record<string, string>) => {
    if (submoduleId && chartsList?.length) {
      if (refetchLoader) {
        setComponentLoader(true);
      }
      const chartsById: any[] = [];
      try {
        await Promise.all(
          tabsChartsList.map(
            async (chart: { id: string; name: string; chartType: string; metadata: string }) => {
              const parsedMetadata = JSON.parse(chart.metadata);
              const response = await getChartDataById(
                submoduleId,
                chart.id,
                filters,
                parsedMetadata?.sort?.fields,
                parsedMetadata?.sort?.order,
              );
              if (response) {
                const { rows, columns, metadata } = formatChartResponseData(response);
                chartsById.push({
                  columns,
                  rows,
                  pricingDetails: response.result.pricingDetails || [],
                  id: chart.id,
                  name: response.name,
                  meta: metadata,
                  rowOrder: response.rowOrder,
                });
              }
            },
          ),
        );
        chartsById.sort((a, b) => a.rowOrder - b.rowOrder);

        setChartData(chartsById);
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setDashboardLoader(false);
        setComponentLoader(false);
        setRefetchLoader(false);
      }
    }
  };

  const formatChartResponseData = (response: any) => {
    let columns = [];
    let rows = [];
    const metadata = response.metadata;

    const { mappedColumns, mappedData } = processCombinedCohortData(
      response.name,
      metadata,
      response.result,
    );
    columns = mappedColumns;
    rows = mappedData;
    rows.forEach((row: any) =>
      row.children?.sort((a: any, b: any) => a.rowLabel.localeCompare(b.rowLabel)),
    );
    return { rows, columns, metadata };
  };

  const fetchPredictionsData = async () => {
    if (selectedGame?.id)
      await getPredictions({
        gameId: selectedGame?.id,
        submoduleId: submoduleId,
      }).then((response) => {
        if (response) setPredictionDetails(response?.data?.data[0]);
      });
  };

  useEffect(() => {
    if (isUpdateFundingPercentageSuccess) {
      setRefetchLoader(true);
      fetchDashboardData({
        game_id: selectedGame?.id,
        predictionRequestId: predictionDetails?.requestId || '',
      });
    }
  }, [isEditProjectionsSuccess, pvxFundingPercentage, isUpdateFundingPercentageSuccess]);

  useEffect(() => {
    setComponentLoader(true);

    if (selectedGame?.id && predictionDetails?.requestId) {
      fetchDashboardData({
        game_id: selectedGame?.id,
        predictionRequestId: predictionDetails?.requestId || '',
      });
    }
  }, [predictionDetails]);

  useEffect(() => {
    setComponentLoader(true);
    fetchPredictionsData();
    dispatch(setDashboardFilter({ FinancialProjections: selectedGame }));
  }, [selectedGame]);

  useEffect(() => {
    if (selectedGame?.id && predictionDetails?.requestId) {
      fetchDashboardData({
        game_id: selectedGame?.id,
        predictionRequestId: predictionDetails?.requestId || '',
      });
    }
  }, [isEditProjectionsSuccess]);

  useEffect(() => {
    if (isPredictionsFetchError) {
      setComponentLoader(false);
    }
  }, [isPredictionsFetchError]);

  const handleFieldEditSave = (updatedValues: ProjectionValueUpdateType[]) => {
    if (updatedValues.length > 0 && predictionDetails?.requestId)
      editProjections({
        submoduleId: submoduleId,
        predictionId: predictionDetails?.requestId,
        body: updatedValues,
      });
  };

  const handleIncrement = () => {
    const newValue = Math.min(pvxFundingPercentage + 10, 80);
    setPvxFundingPercentage(newValue);
    updateFundingPercentage({
      submoduleId,
      predictionId: predictionDetails?.requestId || '',
      body: { value: newValue / 100 },
    });
  };

  const handleDecrement = () => {
    const newValue = Math.max(pvxFundingPercentage - 10, 20);
    setPvxFundingPercentage(newValue);
    updateFundingPercentage({
      submoduleId,
      predictionId: predictionDetails?.requestId || '',
      body: { value: newValue / 100 },
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value >= 20 && value <= 80) {
      setPvxFundingPercentage(value);
      updateFundingPercentage({
        submoduleId: submoduleId,
        predictionId: predictionDetails?.requestId || '',
        body: { value: pvxFundingPercentage / 100 },
      });
    }
  };

  const formattedPricingDetails = chartData[1]?.pricingDetails?.map((item: string) => {
    const regex = /:\s*(-?\d+(\.\d+)?)/; // Matches ": number" pattern
    const match = item.match(regex);

    if (match) {
      const numericValue = parseFloat(match[1]);
      const percentage = (numericValue * 100).toFixed(1) + '%';

      // Replace the original numeric value with the percentage in the string
      return item.replace(regex, `: ${percentage}`);
    }

    return item;
  });

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          padding: '1rem 1rem 0.5rem',
          borderRadius: '0.75rem',
          marginTop: '18px',
          minHeight: '30rem',
        }}
      >
        {isPredictionsFetchError || componentLoader ? (
          <NoData
            isLoading={componentLoader}
            handleRefresh={() => {
              setComponentLoader(true);
              fetchPredictionsData();
            }}
            isError={isPredictionsFetchError}
          />
        ) : (
          <>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
              }}
            >
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                Existing and Future Cohorts
              </Typography>
              <DownloadChart
                submoduleId={submoduleId}
                chartId={chartData[0]?.id}
                filterSelection={{
                  gameId: selectedGame?.id,
                  predictionRequestId: predictionDetails?.requestId || '',
                }}
                fileName={`${chartData[0]?.name}`}
              />
            </Box>
            {
              <FinancialProjectionsTable
                data={chartData[0]?.rows || []}
                columns={chartData[0]?.columns || []}
                meta={chartData[0]?.meta}
                handleFieldSave={handleFieldEditSave}
              />
            }
          </>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          padding: '1rem 1rem 0.5rem',
          borderRadius: '0.75rem',
          marginTop: '18px',
          minHeight: '30rem',
        }}
      >
        {isPredictionsFetchError || componentLoader ? (
          <NoData
            isLoading={componentLoader}
            handleRefresh={() => {
              setComponentLoader(true);
              fetchPredictionsData();
            }}
            isError={isPredictionsFetchError}
          />
        ) : (
          <>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
              }}
            >
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                PVX Funding Scenario
              </Typography>
              <DownloadChart
                submoduleId={submoduleId}
                chartId={chartData[1]?.id}
                filterSelection={{
                  gameId: selectedGame?.id,
                  predictionRequestId: predictionDetails?.requestId || '',
                }}
                fileName={`${chartData[1]?.name}`}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                marginBottom: '1rem',
                gap: '0.5rem',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: '1rem',
                  color: '#6C6C6C',
                  fontWeight: 700,
                  marginRight: '1rem',
                }}
              >
                PVX Funding %
              </Typography>
              <RemoveSharpIcon
                onClick={handleDecrement}
                sx={{
                  color: pvxFundingPercentage === 20 ? '#6C6C6C50' : '#2F736E',
                  width: '2rem',
                  height: '2rem',
                  cursor: 'pointer',
                  pointerEvents: pvxFundingPercentage === 20 ? 'none' : 'auto',
                  border: pvxFundingPercentage === 20 ? '1px solid #6C6C6C50' : '1px solid #2F736E',
                  borderRadius: '2rem',
                  padding: '0.25rem',
                }}
              />
              <TextField
                type="number"
                value={pvxFundingPercentage}
                onChange={handleInputChange}
                inputProps={{ min: 20, max: 80, step: 10 }}
                InputProps={{
                  endAdornment: '%',
                  sx: {
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: '#6C6C6C',
                    textAlign: 'center',
                  },
                }}
                sx={{
                  minWidth: '5rem',
                  textAlign: 'center',
                  pointerEvents: 'none',
                  marginX: '0.5rem',
                  '& .MuiOutlinedInput-input': {
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: '#6C6C6C',
                    height: '0.5rem',
                    textAlign: 'center',
                  },
                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                }}
              />
              <AddSharpIcon
                onClick={handleIncrement}
                sx={{
                  color: pvxFundingPercentage === 80 ? '#6C6C6C50' : '#2F736E',
                  width: '2rem',
                  height: '2rem',
                  cursor: 'pointer',
                  pointerEvents: pvxFundingPercentage === 80 ? 'none' : 'auto',
                  border: pvxFundingPercentage === 80 ? '1px solid #6C6C6C50' : '1px solid #2F736E',
                  borderRadius: '2rem',
                  padding: '0.25rem',
                }}
              />
            </Box>
            <FinancialProjectionsTable
              data={chartData[1]?.rows || []}
              columns={chartData[1]?.columns || []}
              meta={chartData[1]?.meta}
              handleFieldSave={handleFieldEditSave}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                width: '100%',
                gap: isMobile ? '8px' : '0px',
              }}
            >
              <Box
                sx={{
                  color: '#989CA2',
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                  width: isMobile ? '100%' : 'fit-content',
                }}
              >
                {formattedPricingDetails?.map((detail, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                    }}
                  >
                    <Box>{detail}</Box>
                    {index !== formattedPricingDetails?.length - 1 ? (
                      <Circle sx={{ height: '6px', width: '8px' }} />
                    ) : (
                      <></>
                    )}
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '4px',
                  border: '1px solid #D8DBDD',
                  color: '#6C6C6C',
                  fontWeight: '500',
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                  paddingX: '6px',
                  paddingY: '2px',
                  width: 'fit-content',
                }}
              >
                <TrendingUp
                  sx={{
                    color: '#7FBCAD',
                  }}
                />
                : Based on our Projections
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          padding: '1rem 1rem 0.5rem',
          borderRadius: '0.75rem',
          marginTop: '18px',
          minHeight: '30rem',
        }}
      >
        {isPredictionsFetchError || componentLoader ? (
          <NoData
            isLoading={componentLoader}
            handleRefresh={() => {
              setComponentLoader(true);
              fetchPredictionsData();
            }}
            isError={isPredictionsFetchError}
          />
        ) : (
          <>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
              }}
            >
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                EBIDTA Projections
              </Typography>
              <DownloadChart
                submoduleId={submoduleId}
                chartId={chartData[2]?.id}
                filterSelection={{
                  gameId: selectedGame?.id,
                  predictionRequestId: predictionDetails?.requestId || '',
                }}
                fileName={`${chartData[2]?.name}`}
              />
            </Box>
            <CombinedProjectionsChart xLabel="MONTHS" yLabel="EBIDTA" chartData={chartData[2]} />
          </>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          padding: '1rem 1rem 0.5rem',
          borderRadius: '0.75rem',
          marginTop: '18px',
          minHeight: '30rem',
        }}
      >
        {isPredictionsFetchError || componentLoader ? (
          <NoData
            isLoading={componentLoader}
            handleRefresh={() => {
              setComponentLoader(true);
              fetchPredictionsData();
            }}
            isError={isPredictionsFetchError}
          />
        ) : (
          <>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
              }}
            >
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                End Cash Balance Projections
              </Typography>
              <DownloadChart
                submoduleId={submoduleId}
                chartId={chartData[3]?.id}
                filterSelection={{
                  gameId: selectedGame?.id,
                  predictionRequestId: predictionDetails?.requestId || '',
                }}
                fileName={`${chartData[3]?.name}`}
              />
            </Box>
            <CombinedProjectionsChart
              xLabel="MONTHS"
              yLabel="END CASH BALANCE"
              chartData={chartData[3]}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default CombinedCohortsProjections;
